<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);

// Uma cor por prioridade, na mesma escala usada na regua da lateral.
const PRIORITY_RINGS = {
  urgent: 'ring-n-ruby-9',
  high: 'ring-n-amber-9',
  medium: 'ring-n-blue-9',
  low: 'ring-n-slate-8',
};
const priorityRing = computed(
  () => PRIORITY_RINGS[props.conversation?.priority] || ''
);
const priorityTooltip = computed(() => {
  const p = props.conversation?.priority;
  return p ? t(`CONVERSATION.PRIORITY.OPTIONS.${p.toUpperCase()}`) : '';
});
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium } = inbox.value;
  return getInboxIconByType(channelType, medium);
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { labels = [] } = props.conversation;
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold && labels.length === 0
  );
});

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-3 px-3 py-4 transition-all duration-300 ease-in-out cursor-pointer"
    @click="onCardClick"
  >
    <!-- Prioridade como MOLDURA do avatar. As barrinhas ao lado do nome
         disputavam espaco com o selo de caixa e o brilho do brain, e exigiam
         decodificar quantas barras estavam acesas. A borda colorida le-se de
         relance, na coluna que o olho ja percorre. -->
    <div
      v-tooltip.right="priorityTooltip"
      class="flex-shrink-0 rounded-full"
      :class="
        priorityRing ? `p-0.5 ring-2 ring-inset ${priorityRing}` : 'p-0 ring-0'
      "
    >
      <Avatar
        :name="currentContactName"
        :src="currentContactThumbnail"
        :size="24"
        :status="currentContactStatus"
        rounded-full
      />
    </div>
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4
          class="text-base font-medium truncate text-n-slate-12 flex items-center gap-1"
        >
          <Icon
            v-if="conversation.is_group"
            icon="i-lucide-users"
            class="size-3.5 text-n-slate-9 flex-shrink-0"
          />
          {{
            conversation.is_group
              ? conversation.additional_attributes?.group_name ||
                currentContactName
              : currentContactName
          }}
        </h4>
        <div class="flex items-center gap-2">
          <Icon
            v-if="conversation.additional_attributes?.has_brain_insight"
            v-tooltip="$t('CAPTAIN.BRAIN.HAS_INSIGHT')"
            icon="i-lucide-sparkles"
            class="size-3 text-iris-7 flex-shrink-0"
          />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :account-labels="accountLabels"
      />
    </div>
  </div>
</template>
